import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

/**
 * Phase 26 — "Restablecer contraseña"
 * Reads `?token=` from the URL, POSTs to `/api/auth/reset-password` with the
 * user's new password. On success shows a Spanish confirmation and redirects
 * to /entrar after 3 s. On error (invalid / expired / used) shows the
 * specific backend detail plus a link back to /olvide-contrasena.
 */
export default function RestablecerContrasenaPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (status === "success") {
      const t = setTimeout(() => navigate("/entrar", { replace: true }), 3000);
      return () => clearTimeout(t);
    }
  }, [status, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!token) {
      setStatus("error");
      setErrorMsg("Falta el token. Solicita un nuevo enlace.");
      return;
    }
    if (pwd.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setStatus("success");
      toast.success("Contraseña actualizada");
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setStatus("error");
      if (code === 429) {
        setErrorMsg("Demasiados intentos. Inténtalo más tarde.");
      } else if (typeof detail === "string") {
        setErrorMsg(detail);
      } else {
        setErrorMsg("No se pudo cambiar la contraseña. Solicita un enlace nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="restablecer-page"
      className="mx-auto max-w-md px-6 py-16 sm:py-24"
    >
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-[#00953B] text-white grid place-items-center font-black">
            M
          </div>
          <span className="text-2xl font-black tracking-tight">
            Merca<span className="text-[#00953B]">Go</span>
          </span>
        </Link>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
        <h1 className="text-2xl font-black tracking-tight">Restablecer contraseña</h1>

        {status === "success" ? (
          <div
            data-testid="restablecer-success"
            className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-4 text-sm leading-relaxed"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-700" />
              <div>
                <div className="font-semibold">¡Contraseña actualizada!</div>
                Ya puedes iniciar sesión. Te redirigimos en unos segundos.
              </div>
            </div>
          </div>
        ) : status === "error" ? (
          <div
            data-testid="restablecer-error"
            className="mt-6 rounded-xl bg-red-50 border border-red-200 text-red-900 px-4 py-4 text-sm leading-relaxed space-y-3"
          >
            <div className="flex items-start gap-2">
              <XCircle className="h-4 w-4 mt-0.5 text-red-700" />
              <div>
                <div className="font-semibold">No se pudo cambiar la contraseña</div>
                {errorMsg}
              </div>
            </div>
            <Link
              to="/olvide-contrasena"
              data-testid="restablecer-request-again-link"
              className="text-red-800 font-semibold hover:underline text-sm inline-block"
            >
              Volver a solicitar el enlace →
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-500 mt-1">
              Elige una contraseña nueva para tu cuenta MercaGo. Debe tener al menos 8 caracteres.
            </p>
            <form
              onSubmit={submit}
              className="mt-6 space-y-4"
              data-testid="restablecer-form"
            >
              <div>
                <Label htmlFor="pwd" className="text-sm">Nueva contraseña</Label>
                <Input
                  id="pwd"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  data-testid="restablecer-pwd-input"
                  className="mt-1 h-11"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-sm">Confirmar contraseña</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="restablecer-confirm-input"
                  className="mt-1 h-11"
                  placeholder="Repite la contraseña"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !pwd || !confirm}
                data-testid="restablecer-submit-btn"
                className="w-full h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold"
              >
                {submitting ? "Cambiando…" : "Cambiar contraseña"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
